import { useEffect } from "react";

interface OpenChannelOpts {
  workspaceId?: string | null;
  targetMessageId?: string;
  targetThreadId?: string;
}

type OpenChannelFn = (channelId: string, opts?: OpenChannelOpts) => void;

interface DeepLinkDetail {
  channelId: string;
  messageId: string | null;
  threadId?: string | null;
}

interface ElectronApi {
  onShellOpenChannel?: (
    callback: (detail: DeepLinkDetail) => void,
  ) => () => void;
  onFocusChatSideBySide?: (callback: () => void) => () => void;
}

/**
 * Listens for channel-navigation intents from two sources:
 *
 * 1. **`ano:open-channel-from-shell` window event** — dispatched by the
 *    xterm link handlers when the operator clicks an in-app channel
 *    deep-link inside the shell. This is the primary path: the web-app
 *    deep-link the `/unread` workflow emits
 *    (`<web-origin>/?channel=<id>&message=<id>` — localhost:1420 in dev,
 *    app.ano.dev in prod). The addon runs outside the React tree, so the
 *    window event bridges the two. See `shell-deep-link-router.ts`.
 *
 * 2. **`ano:open-channel` IPC** — dispatched by the Electron main process
 *    when an `ano://channel/<id>?message=<msg>` URL arrives via
 *    `app.on("open-url", ...)`. This is the platform-level `ano:` deep-link
 *    path: an external opener (email link, a paste, or the future web→app
 *    "open in app" handoff) hands a channel URL to the registered desktop
 *    app. The preload re-exposes it as `window.__electronApi.onShellOpenChannel`.
 *
 * Both paths fan in to the same `openChannelWithContext` call, so
 * navigation, scroll-to-message, and ring-flash behave identically
 * regardless of which path triggered them.
 */
export function useShellDeepLinkListener(
  openChannelWithContext: OpenChannelFn,
): void {
  useEffect(() => {
    function navigate(detail: DeepLinkDetail | undefined | null) {
      if (!detail?.channelId) return;
      openChannelWithContext(
        detail.channelId,
        detail.messageId ? { targetMessageId: detail.messageId } : undefined,
      );
    }

    // Source 1: xterm window event.
    const windowHandler = (e: Event) => {
      navigate((e as CustomEvent<DeepLinkDetail>).detail);
    };
    window.addEventListener("ano:open-channel-from-shell", windowHandler);

    // Source 2: Electron IPC re-dispatched by preload. Optional —
    // absent in web builds and tests without the preload bridge.
    const electronApi = (window as unknown as { __electronApi?: ElectronApi })
      .__electronApi;
    const unsubscribeChannelIpc = electronApi?.onShellOpenChannel?.(navigate);
    // Side-by-side IPC → re-dispatch as the same window event the xterm
    // addon fires today. `useViewMode` already listens for it; this keeps
    // the source-agnostic boundary at the IPC bridge instead of asking
    // every interested hook to know about __electronApi.
    const unsubscribeFocusIpc = electronApi?.onFocusChatSideBySide?.(() => {
      window.dispatchEvent(new CustomEvent("ano:focus-chat-side-by-side"));
    });

    return () => {
      window.removeEventListener("ano:open-channel-from-shell", windowHandler);
      unsubscribeChannelIpc?.();
      unsubscribeFocusIpc?.();
    };
  }, [openChannelWithContext]);
}
